import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import type { BuildRow, SessionRow, EhrRow, ConflictRow, JiraIssue, KbArticle, AuditLogRow } from '../lib/api.js';

type ResultType = 'build' | 'session' | 'ehr' | 'conflict' | 'ticket' | 'kb' | 'audit';

interface Result {
  id: string;
  type: ResultType;
  label: string;
  sub: string;
  to: string;
  score: number;
}

const TYPE_ICON: Record<ResultType, string> = {
  build:    '⎔',
  session:  '⧉',
  ehr:      '⬢',
  conflict: '⚠',
  ticket:   '⬡',
  kb:       '◎',
  audit:    '◷',
};

const TYPE_LABEL: Record<ResultType, string> = {
  build:    'build',
  session:  'session',
  ehr:      'ehr',
  conflict: 'conflict',
  ticket:   'ticket',
  kb:       'article',
  audit:    'audit',
};

function rankScore(query: string, text: string): number {
  const q = query.toLowerCase();
  const t = (text ?? '').toLowerCase();
  if (!t) return 0;
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (t.includes(q)) return 50;
  return 0;
}

function best(...scores: number[]) {
  return Math.max(...scores);
}

export function CommandBar() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
        setQuery('');
        setSelected(0);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const results = useMemo((): Result[] => {
    if (!query.trim()) return [];
    const q = query.trim();
    const out: Result[] = [];

    const builds = (qc.getQueryData(['builds']) as { builds: BuildRow[] } | undefined)?.builds ?? [];
    for (const b of builds) {
      const s = best(rankScore(q, b.displayName), rankScore(q, b.namespace), rankScore(q, b.region));
      if (s > 0) out.push({ id: `build-${b.id}`, type: 'build', label: b.displayName, sub: `${b.region} · ${b.namespace}`, to: `/builds/${b.id}`, score: s });
    }

    const sessions = (qc.getQueryData(['sessions']) as { sessions: SessionRow[] } | undefined)?.sessions ?? [];
    for (const s of sessions) {
      const sc = best(rankScore(q, s.title ?? ''), rankScore(q, s.id), rankScore(q, s.status));
      if (sc > 0) out.push({ id: `session-${s.id}`, type: 'session', label: s.title ?? s.id.slice(0, 8), sub: s.status, to: `/sessions/${s.id}`, score: sc });
    }

    const ehrs = (qc.getQueryData(['ehrs']) as { ehrs: EhrRow[] } | undefined)?.ehrs ?? [];
    for (const e of ehrs) {
      const s = best(rankScore(q, e.displayName), rankScore(q, e.slug), rankScore(q, e.loginUrl ?? ''));
      if (s > 0) out.push({ id: `ehr-${e.id}`, type: 'ehr', label: e.displayName, sub: e.slug, to: `/ehrs/${e.id}`, score: s });
    }

    const conflicts = (qc.getQueryData(['conflicts']) as { conflicts: ConflictRow[] } | undefined)?.conflicts ?? [];
    for (const c of conflicts) {
      const s = best(rankScore(q, c.kind), rankScore(q, c.severity), rankScore(q, c.sessionTitle ?? ''), rankScore(q, c.status));
      if (s > 0) out.push({ id: `conflict-${c.id}`, type: 'conflict', label: c.sessionTitle ?? c.kind, sub: `${c.severity} · ${c.kind}`, to: `/sessions/${c.sessionId}`, score: s });
    }

    const jira = (qc.getQueryData(['jira']) as { issues: JiraIssue[] } | undefined)?.issues ?? [];
    for (const t of jira) {
      const s = best(rankScore(q, t.summary), rankScore(q, t.key), rankScore(q, t.clientEmr ?? ''), rankScore(q, t.assignee ?? ''));
      if (s > 0) out.push({ id: `ticket-${t.key}`, type: 'ticket', label: `${t.key}: ${t.summary}`, sub: `${t.status} · ${t.assignee ?? 'unassigned'}`, to: '/tickets', score: s });
    }

    const kb = (qc.getQueryData(['kb']) as { articles: KbArticle[] } | undefined)?.articles ?? [];
    for (const a of kb) {
      const s = best(rankScore(q, a.title), rankScore(q, a.body ?? ''));
      if (s > 0) out.push({ id: `kb-${a.id}`, type: 'kb', label: a.title, sub: `by ${a.authorName ?? 'unknown'}`, to: `/kb/${a.id}`, score: s });
    }

    const auditLogs = (qc.getQueryData(['audit-logs']) as { logs: AuditLogRow[] } | undefined)?.logs ?? [];
    for (const l of auditLogs) {
      const s = best(rankScore(q, l.action), rankScore(q, l.userEmail ?? ''), rankScore(q, l.resource ?? ''));
      if (s > 0) out.push({ id: `audit-${l.id}`, type: 'audit', label: l.action, sub: `${l.userEmail ?? 'system'} · ${l.resource ?? ''}`, to: '/settings/audit-logs', score: s });
    }

    return out.sort((a, b) => b.score - a.score).slice(0, 15);
  }, [query, qc]);

  // Group by type for display
  const grouped = useMemo(() => {
    const groups: Record<string, Result[]> = {};
    for (const r of results) {
      (groups[r.type] ??= []).push(r);
    }
    return groups;
  }, [results]);

  const flatResults = useMemo(() => {
    return Object.values(grouped).flat();
  }, [grouped]);

  useEffect(() => { setSelected(0); }, [results]);

  const go = (r: Result) => {
    void navigate({ to: r.to as '/' });
    setOpen(false);
    setQuery('');
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
      onClick={() => setOpen(false)}
    >
      <div
        className="w-[600px] rounded-2xl overflow-hidden"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--glass-border)', boxShadow: '0 32px 80px rgba(0,0,0,0.7)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border">
          <span className="text-lg text-text-dimmed shrink-0">⌕</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search everything…"
            className="flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-dimmed"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setSelected((s) => Math.min(s + 1, flatResults.length - 1)); }
              if (e.key === 'ArrowUp') { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)); }
              if (e.key === 'Enter' && flatResults[selected]) go(flatResults[selected]);
            }}
          />
          <kbd className="text-[10px] text-text-dimmed border border-border rounded px-1.5 py-0.5 shrink-0">esc</kbd>
        </div>

        {/* Results */}
        {flatResults.length > 0 && (
          <div className="max-h-[420px] overflow-y-auto py-2">
            {Object.entries(grouped).map(([type, items]) => (
              <div key={type}>
                <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-text-dimmed opacity-60">
                  {TYPE_LABEL[type as ResultType]}s
                </div>
                {items.map((r) => {
                  const globalIdx = flatResults.indexOf(r);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-left transition-colors"
                      style={{ background: globalIdx === selected ? 'var(--bg-tertiary)' : 'transparent' }}
                      onMouseEnter={() => setSelected(globalIdx)}
                      onClick={() => go(r)}
                    >
                      <span className="text-base shrink-0 w-5 text-center" style={{ color: 'var(--text-dimmed)' }}>
                        {TYPE_ICON[r.type]}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-text-primary truncate">{r.label}</div>
                        <div className="text-xs text-text-dimmed truncate">{r.sub}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {query.trim() && flatResults.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-text-dimmed">No results for "{query}"</div>
        )}

        {!query.trim() && (
          <div className="px-4 py-5 flex flex-wrap gap-2">
            {(['build', 'session', 'ehr', 'conflict', 'ticket', 'kb', 'audit'] as ResultType[]).map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-dimmed)', border: '1px solid var(--border)' }}>
                <span>{TYPE_ICON[t]}</span>{TYPE_LABEL[t]}s
              </span>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// Exported so Sidebar can trigger it without prop drilling
export function useCommandBar() {
  const open = () => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
  return { open };
}
